import { Link } from 'react-router';
import { Image } from '@rocco/components/image';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';

interface RecentlyViewedProductTileProps {
    product: ProductTileViewModel;
}

/**
 * Simplified product tile for the Recently Viewed section on Products page.
 * Displays only image, product name, and supplier name (no price, no follow button).
 * Matches Figma design node 20625:2454.
 */
export const RecentlyViewedProductTile = ({
    product,
}: RecentlyViewedProductTileProps) => {
    const tracker = useTracker();
    const primaryImage = product.images?.[0];

    const handleClick = () => {
        trackTileMediaDetailClick(tracker, {
            itemID: product.id,
            itemType: 'ProductTile',
            itemName: product.title,
        });
        tracker.log('recentlyViewedTileClick', {
            url: new URL(window.location.href),
            data: {
                LibraryItemID: product.id,
                ProfessionalID: product.professional?.id,
            },
        });
    };

    return (
        <Link
            to={product.link}
            prefetch="intent"
            onClick={handleClick}
            className="group flex flex-col gap-[0.9375rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
            {/* Square image - 8px border radius */}
            <div
                className={cn(
                    'relative aspect-square w-full overflow-hidden rounded-2',
                    'bg-[#FAFAFA]',
                )}
            >
                {primaryImage && (
                    <Image
                        src={primaryImage.src}
                        alt={primaryImage?.alt || product.title}
                        fill
                        fit={primaryImage.multiplied ? 'contain' : 'cover'}
                        multiplied={primaryImage.multiplied}
                        sizes="(max-width: 768px) 50vw, 342px"
                        className="transition-transform duration-300 group-hover:scale-105"
                    />
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-2 bg-gray-900/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>

            {/* Product info - simplified (no price, no follow) */}
            <div className="flex flex-col gap-0.5">
                <TextTooltip content={product.title}>
                    <p className="line-clamp-1 text-4.5 font-medium leading-[1.3] tracking-[0.36px] text-gray-900">
                        {product.title}
                    </p>
                </TextTooltip>
                <TextTooltip content={product.professional?.name ?? ''}>
                    <p className="line-clamp-1 text-4 font-medium leading-[1.3] tracking-[0.32px] text-gray-500">
                        {product.professional?.name}
                    </p>
                </TextTooltip>
            </div>
        </Link>
    );
};
